import type { ServerError, UserVo } from 'models';

export type PerpsOnlyStatus = 'unknown' | 'standard' | 'perpsOnly';
export type KnownAccountPolicy = Exclude<PerpsOnlyStatus, 'unknown'>;

export type AccountPolicyAvailability =
    | { phase: 'loading' }
    | { phase: 'error'; error: ServerError }
    | { phase: 'ready'; status: KnownAccountPolicy };

const STANDARD_POLICY_READY: AccountPolicyAvailability = { phase: 'ready', status: 'standard' };
const PERPS_ONLY_POLICY_READY: AccountPolicyAvailability = { phase: 'ready', status: 'perpsOnly' };

export type AccountPolicyProfileLoadState = {
    phase: 'idle' | 'loading' | 'ready' | 'error';
    userId: UserVo['user_id'] | null;
    error: ServerError | null;
};

type PerpsOnlyAuthState = {
    isLoggedIn: boolean;
    userId: UserVo['user_id'] | null;
    user: Pick<UserVo, 'user_id' | 'perps_only'> | null;
    accountPolicyInvalidatedForUserId?: UserVo['user_id'] | null;
};

/**
 * Authenticated account policy derived from the canonical user profile.
 * Only an explicit flag on the active user's profile enables Perps-only
 * restrictions. Missing, stale, or invalidated profile data remains standard;
 * the backend is the authoritative enforcement boundary while it refreshes.
 */
export function selectPerpsOnlyStatus(state: PerpsOnlyAuthState): PerpsOnlyStatus {
    if (!state.isLoggedIn) {
        return 'standard';
    }

    if (
        !state.user ||
        state.user.user_id !== state.userId ||
        state.accountPolicyInvalidatedForUserId === state.userId
    ) {
        return 'standard';
    }

    return state.user.perps_only ? 'perpsOnly' : 'standard';
}

/**
 * Unknown policy is intentionally normalized to standard so profile loading
 * or refresh failures never block normal account flows by themselves.
 */
export function resolveAccountPolicyAvailability(
    policyStatus: PerpsOnlyStatus,
    _userId: UserVo['user_id'] | null,
    _profileLoadState: AccountPolicyProfileLoadState,
): AccountPolicyAvailability {
    return policyStatus === 'perpsOnly' ? PERPS_ONLY_POLICY_READY : STANDARD_POLICY_READY;
}
